import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import PrintIcon from "@mui/icons-material/Print";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { toast } from "react-toastify";
import CertificateCanvas from "./CertificateCanvas";
import CertificateSettingsPanel from "./CertificateSettingsPanel";
import CertificateToolbar from "./CertificateToolbar";
import {
  CertificatePageSettings,
  CertificateTemplate,
  getCssAtPageSize,
  getJsPdfPageOptions,
  getPageSize,
} from "./certificateTypes";
import { CERTIFICATE_SAMPLE_DATA, mergeClinicPreviewData } from "./certificateSampleData";
import { cloneCertificateTemplate, getDefaultTemplateById } from "./certificateTemplates";
import {
  createTemplateId,
  findTemplate,
  hydrateCertificateStore,
  loadCertificateStore,
  printCertificateSheet,
  saveCertificateStore,
} from "./certificateStorage";
import { useLoggedInClinic } from "./useLoggedInClinic";
import { resolveCertificateLogo } from "./getLoggedInClinic";
import baseUrl from "../../../../api/baseUrl";
import "./certificatePrint.css";

function waitForPaint() {
  return new Promise<void>((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
  });
}

function CertificateDesigner() {
  const clinic = useLoggedInClinic();
  const stageRef = useRef<HTMLDivElement>(null);
  const paperRef = useRef<HTMLDivElement>(null);

  const [store, setStore] = useState(loadCertificateStore);
  const storeRef = useRef(store);
  storeRef.current = store;
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [zoom, setZoom] = useState(0.75);
  const [mobilePane, setMobilePane] = useState<"design" | "preview">("preview");
  const [printing, setPrinting] = useState(false);

  const active = findTemplate(store.templates, store.activeId);

  useEffect(() => {
    saveCertificateStore(store);
  }, [store]);

  useEffect(() => {
    baseUrl
      .get("/clinic/getBillData")
      .then((response) => {
        const saved = response?.data?.data?.certificatePrint;
        if (!saved) return;
        setStore(hydrateCertificateStore(saved));
      })
      .catch(() => {
        /* keep local store */
      });
  }, []);

  const previewPage = useMemo(
    () => ({
      ...active.page,
      logoSrc: resolveCertificateLogo(clinic.printImages, active.page.logoSrc),
    }),
    [active.page, clinic.printImages]
  );

  const previewData = useMemo(
    () => mergeClinicPreviewData(CERTIFICATE_SAMPLE_DATA, clinic),
    [clinic]
  );

  const updateActive = useCallback(
    (updater: (template: CertificateTemplate) => CertificateTemplate) => {
      setStore((current) => ({
        ...current,
        templates: current.templates.map((template) =>
          template.id === current.activeId ? updater(template) : template
        ),
      }));
    },
    []
  );

  const persistToClinic = async (nextStore = storeRef.current) => {
    await baseUrl.patch("/clinic/saveCertificatePrint", {
      certificatePrint: nextStore,
    });
  };

  const fitToScreen = useCallback(() => {
    const stage = stageRef.current;
    if (!stage) return;
    const size = getPageSize(active.page.orientation, active.page.size);
    const available = stage.clientWidth - 48;
    const pagePx = (size.widthMm * 96) / 25.4;
    setZoom(Math.max(0.35, Math.min(1.2, available / pagePx)));
  }, [active.page.orientation]);

  const selected = active.elements.find((item) => item.id === selectedId) ?? null;

  const handlePrint = async () => {
    setSelectedId(null);
    setPrinting(true);
    await waitForPaint();
    await printCertificateSheet(paperRef.current);
    setPrinting(false);
  };

  const handleDownloadPdf = async () => {
    const paper = paperRef.current;
    if (!paper) return;
    const previousZoom = zoom;
    setSelectedId(null);
    setPrinting(true);
    setZoom(1);
    await waitForPaint();
    try {
      const canvas = await html2canvas(paper, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
        logging: false,
      });
      const pdfOpts = getJsPdfPageOptions(active.page.orientation, active.page.size);
      const pdf = new jsPDF({
        orientation: pdfOpts.orientation,
        unit: pdfOpts.unit,
        format: pdfOpts.format,
      });
      pdf.addImage(
        canvas.toDataURL("image/png"),
        "PNG",
        0,
        0,
        pdfOpts.page.widthMm,
        pdfOpts.page.heightMm,
        "",
        "FAST"
      );
      pdf.save(`${active.name.replace(/\s+/g, "-").toLowerCase()}.pdf`);
    } catch {
      toast.error("Could not prepare the PDF preview.");
    } finally {
      setZoom(previousZoom);
      setPrinting(false);
    }
  };

  return (
    <div className="certificate-designer">
      <style>
        {`@media print { @page { size: ${getCssAtPageSize(active.page.orientation, active.page.size)}; margin: 0; } }`}
      </style>
      <div className="certificate-mobile-tabs" data-no-print="true">
        <button
          type="button"
          className={mobilePane === "design" ? "is-active" : ""}
          onClick={() => setMobilePane("design")}
        >
          Design
        </button>
        <button
          type="button"
          className={mobilePane === "preview" ? "is-active" : ""}
          onClick={() => setMobilePane("preview")}
        >
          Preview
        </button>
      </div>

      <div className={`certificate-designer-grid is-${mobilePane}`}>
        <aside className="certificate-panel certificate-controls" data-no-print="true">
          <div className="certificate-panel-header">
            <span className="print-panel-eyebrow">Design controls</span>
            <h2>Certificate designer</h2>
            <p>Customise the live preview. Placeholders stay bound to sample data until certificates are issued.</p>
          </div>
          <CertificateSettingsPanel
            templates={store.templates}
            activeId={store.activeId}
            defaultId={store.defaultId}
            page={active.page}
            elements={active.elements}
            selected={selected}
            onSelectTemplate={(id) => {
              setStore((current) => ({ ...current, activeId: id }));
              setSelectedId(null);
            }}
            onRename={(name) => updateActive((template) => ({ ...template, name }))}
            onSaveAsNew={() => {
              const copy = cloneCertificateTemplate(active, {
                id: createTemplateId(),
                name: `${active.name} copy`,
              });
              setStore((current) => ({
                ...current,
                templates: [...current.templates, copy],
                activeId: copy.id,
              }));
              toast.success("Created a new template. Click Save template to store it for this clinic.");
            }}
            onDuplicate={() => {
              const copy = cloneCertificateTemplate(active, {
                id: createTemplateId(),
                name: `${active.name} copy`,
              });
              setStore((current) => ({
                ...current,
                templates: [...current.templates, copy],
                activeId: copy.id,
              }));
              toast.success("Template duplicated. Click Save template to store it for this clinic.");
            }}
            onReset={() => {
              const builtin = getDefaultTemplateById(active.id) ?? getDefaultTemplateById(store.defaultId);
              if (!builtin) return;
              updateActive(() => ({
                ...builtin,
                id: active.id,
                name: active.name,
              }));
              toast.info("Reset to the built-in layout for this template.");
            }}
            onSetDefault={() => {
              setStore((current) => ({ ...current, defaultId: current.activeId }));
              toast.success("Set as the default certificate template. Click Save template to store it for this clinic.");
            }}
            onPageChange={(patch: Partial<CertificatePageSettings>) =>
              updateActive((template) => ({ ...template, page: { ...template.page, ...patch } }))
            }
            onElementChange={(id, patch) =>
              updateActive((template) => ({
                ...template,
                elements: template.elements.map((item) =>
                  item.id === id ? { ...item, ...patch } : item
                ),
              }))
            }
            onToggleField={(ids, visible) =>
              updateActive((template) => ({
                ...template,
                elements: template.elements.map((item) =>
                  ids.includes(item.id) ? { ...item, visible } : item
                ),
              }))
            }
            onSelectElement={setSelectedId}
            onSave={() => {
              persistToClinic(storeRef.current)
                .then(() => toast.success("Template saved for this clinic."))
                .catch(() => toast.error("Could not save the certificate template."));
            }}
          />
        </aside>

        <section className="certificate-panel certificate-preview">
          <div className="certificate-panel-header" data-no-print="true">
            <span className="print-panel-eyebrow">Certificate print preview</span>
            <h2>Medical certificate</h2>
            <p>Zoom is editor-only. Print uses the selected paper size without controls or guides.</p>
          </div>
          <div className="certificate-preview-body">
            <div data-no-print="true">
              <CertificateToolbar zoom={zoom} onZoomChange={setZoom} onFit={fitToScreen} />
            </div>
            <div className="certificate-stage" ref={stageRef}>
              <CertificateCanvas
                page={previewPage}
                elements={active.elements}
                selectedId={printing ? null : selectedId}
                previewData={previewData}
                interactive={!printing}
                zoom={printing ? 1 : zoom}
                paperRef={paperRef}
                onSelect={setSelectedId}
                onElementsChange={(updater) =>
                  updateActive((template) => ({
                    ...template,
                    elements: updater(template.elements),
                  }))
                }
              />
            </div>
            <div className="certificate-footer-bar mt-4" data-no-print="true">
              <button type="button" className="certificate-btn" onClick={handleDownloadPdf}>
                <PictureAsPdfIcon sx={{ fontSize: 16 }} /> Download PDF
              </button>
              <button type="button" className="certificate-btn certificate-btn-primary" onClick={handlePrint}>
                <PrintIcon sx={{ fontSize: 16 }} /> Print
              </button>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

export default CertificateDesigner;
